import type { FC, ReactNode } from 'react'

import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { TransitionLink } from '@/components/generals/transition-link'
import { Enter, HeroTitle } from '@/components/motion/hero-title'

interface PageHeroProps {
    eyebrow: string
    title: string
    description?: ReactNode
    backLink?: { label: string, href: string }
    actions?: ReactNode
    aside?: ReactNode
    accent?: string[]
    children?: ReactNode
    className?: string
    titleClassName?: string
}

export const PageHero: FC<PageHeroProps> = ({
    eyebrow,
    title,
    description,
    backLink,
    actions,
    aside,
    accent,
    children,
    className,
    titleClassName
}): ReactNode => {
    return (
        <section className={cn('relative overflow-hidden pb-16 pt-36 md:pb-24 md:pt-44 lg:pt-48', className)}>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute -right-40 -top-40 size-[42rem] rounded-full bg-ember/[0.07] blur-3xl'
            />

            <Container className='relative'>
                {backLink && (
                    <Enter delay={0}>
                        <TransitionLink
                            href={backLink.href}
                            className='group mb-10 inline-flex items-center gap-2 text-sm font-medium text-slate transition-colors hover:text-ink'
                        >
                            <span className='flex size-8 items-center justify-center rounded-full border border-ink/12 transition-colors group-hover:border-ink/30'>
                                <ArrowLeft className='size-3.5 transition-transform duration-300 group-hover:-translate-x-0.5' aria-hidden='true' />
                            </span>
                            {backLink.label}
                        </TransitionLink>
                    </Enter>
                )}

                <div className={cn('grid gap-12', aside && 'lg:grid-cols-12 lg:items-end lg:gap-10')}>
                    <div className={cn(aside ? 'lg:col-span-7' : 'max-w-5xl')}>
                        <Enter delay={60}>
                            <Eyebrow>{eyebrow}</Eyebrow>
                        </Enter>

                        <HeroTitle
                            text={title}
                            accent={accent}
                            className={cn('mt-6 text-h1 font-semibold text-ink', titleClassName)}
                        />

                        {description && (
                            <Enter delay={420} className='mt-7 max-w-2xl text-lead text-pretty text-slate'>
                                {description}
                            </Enter>
                        )}

                        {actions && (
                            <Enter delay={540} className='mt-10 flex flex-wrap items-center gap-3'>
                                {actions}
                            </Enter>
                        )}
                    </div>

                    {aside && (
                        <Enter delay={300} className='lg:col-span-5'>
                            {aside}
                        </Enter>
                    )}
                </div>

                {children}
            </Container>
        </section>
    )
}
